import { createTheme } from '@mui/material/styles';

const breakpointsValues = {
  xs: 0,
  sm: 600,
  md: 900,
  mdLg: 1000,
  lg: 1200,
  xl: 1536,
};

export function getAppTheme(mode = 'dark') {
  const isDark = mode === 'dark';

  return createTheme({
    breakpoints: {
      values: breakpointsValues,
    },
    palette: {
      mode: isDark ? 'dark' : 'light',
      background: {
        default: isDark ? '#131417' : '#FFFFFF',
        paper: isDark ? '#1F2127' : '#FFFFFF',
        elevated: isDark ? '#272A32' : '#F8F9FA',
      },
      text: {
        primary: isDark ? '#F5F6F8' : '#272727',
        secondary: isDark ? '#A0A6B2' : '#757575',
        muted: isDark ? '#6B7280' : '#8F8F8F',
      },
      primary: {
        light: isDark ? '#A0A6B2' : '#8F8F8F',
        main: isDark ? '#F5F6F8' : '#272727',
        dark: isDark ? '#F5F6F8' : '#272727',
        darkest: isDark ? '#131417' : '#1b1c1d',
        contrastText: '#ffffff',
      },
      secondary: {
        light: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(217, 217, 217, 0.25)',
        main: isDark ? '#272A32' : 'rgb(180,180,180)',
        dark: isDark ? '#1F2127' : '#272727',
        contrastText: isDark ? '#F5F6F8' : '#f8f8f8',
      },
      tertiary: {
        light: isDark ? '#2E323B' : '#D9D9D9',
        main: isDark ? '#F5F6F8' : '#272727',
        dark: isDark ? '#1F2127' : '#272727',
        darkest: isDark ? '#131417' : '#1b1c1d',
        contrastText: isDark ? '#A0A6B2' : '#75757575',
      },
      danger: {
        light: '#8b3bc1',
        main: '#B20B0B',
        dark: '#4d077c',
        contrastText: '#fff',
      },
    },
    typography: {
      fontFamily: ['Montserrat', 'Roboto', 'Helvetica', 'sans-serif'].join(','),
      fontSize: 14,
      fontWeightRegular: 500,
      fontWeightMedium: 700,
      color: isDark ? '#F5F6F8' : '#272727',
      brand: {
        fontFamily: ['Lato', 'Helvetica Neue', 'Arial', 'Helvetica', 'sans-serif'].join(','),
        fontSize: '26px',
        lineHeight: '32px',
        fontWeight: '700',
        color: isDark ? '#F5F6F8' : '#272727',
        display: 'block',
      },
      h1: {
        fontSize: '22px',
        lineHeight: '32px',
        fontWeight: '400',
        color: isDark ? '#F5F6F8' : '#272727',
      },
      h2: {
        fontSize: '22px',
        lineHeight: '32px',
        fontWeight: '500',
        textTransform: 'uppercase',
        color: isDark ? '#F5F6F8' : '#272727',
      },
      h3: {
        fontSize: '18px',
        lineHeight: '32px',
        fontWeight: '400',
        marginBottom: '15px',
        color: isDark ? '#F5F6F8' : '#272727',
        '@media (max-width:600px)': {
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
      h4: {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: '500',
        color: isDark ? '#F5F6F8' : '#272727',
      },
      h5: {
        fontSize: '12px',
        lineHeight: '20px',
        fontWeight: '500',
        color: isDark ? '#F5F6F8' : '#272727',
      },
      body1: {
        fontSize: '14px',
        lineHeight: '20px',
        color: isDark ? '#A0A6B2' : '#757575',
      },
      body2: {
        fontSize: '16px',
        lineHeight: '20px',
        fontWeight: 400,
        color: isDark ? '#F5F6F8' : '#272727',
      },
      byline: {
        fontSize: '14px',
        lineHeight: '18px',
        fontWeight: 700,
        color: isDark ? '#F5F6F8' : '#272727',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: ({ ownerState, theme: t }) => ({
            borderRadius: 0,
            boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.15)',
            ...(ownerState.variant === 'contained' && {
              backgroundColor: isDark ? '#282C35' : '#272727',
              color: '#F5F6F8',
              border: isDark ? '1px solid #4B5260' : 'none',
              '&:hover': {
                backgroundColor: isDark ? '#323742' : '#3C3C3C',
                borderColor: isDark ? '#6B7280' : undefined,
              },
            }),
            ...(ownerState.variant === 'contained' &&
              ownerState.color === 'secondary' && {
                backgroundColor: isDark ? '#272A32' : 'rgb(180,180,180)',
                color: isDark ? '#F5F6F8' : '#272727',
                '&:hover': {
                  backgroundColor: isDark ? '#2E323B' : 'rgb(180,180,180)',
                },
              }),
            ...(ownerState.variant === 'outlined' && {
              borderColor: isDark ? '#4B5260' : undefined,
              color: isDark ? '#F5F6F8' : undefined,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
              },
            }),
            ...(ownerState.variant === 'shadowed' && {
              marginLeft: '25px',
              border: isDark ? '1px solid #4B5260' : '1px solid #D1D5DB',
              backgroundColor: isDark ? '#1F2127' : '#FFFFFF',
              color: isDark ? '#F5F6F8' : '#272727',
              borderRadius: '4px',
              boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)' : '0px 3px 4px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: isDark ? '#272A32' : t.palette.secondary.light,
                borderColor: isDark ? '#6B7280' : '#9CA3AF',
              },
              [t.breakpoints.down('sm')]: {
                marginLeft: '0',
                marginBottom: '1em',
                width: '100%',
              },
            }),
          }),
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
        styleOverrides: {
          root: {
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            margin: 0,
            border: 0,
            color: isDark ? '#F5F6F8' : '#272727',
            maxWidth: '100%',
          },
        },
      },
      MuiGrid2: {
        styleOverrides: {
          root: {
            alignItems: 'stretch',
            '> .MuiGrid2-root > .MuiGrid2-root': {
              boxShadow: isDark
                ? '0px 4px 6px rgba(0, 0, 0, 0.4)'
                : '0px 4px 4px rgba(0, 0, 0, 0.15)',
              padding: 0,
              height: '100%',
            },
            '> .MuiGrid2-root > .add-item': {
              padding: '1em',
              minHeight: '200px',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#272A32' : 'white',
            border: isDark ? '1px solid #4B5260' : '1px solid black',
            color: isDark ? '#F5F6F8' : 'black',
            fontSize: '12px',
            fontWeight: 400,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  });
}

const theme = getAppTheme('dark');
export default theme;
